import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  makeStyles,
  Chip,
  IconButton,
} from "@material-ui/core";
import { Close, Settings, Edit } from "@material-ui/icons";
import { getApiBaseUrl } from "../config";
import { useAuth } from "../context/ApiKeyContext";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid var(--ikaro-gray-medium)",
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
  titleLeft: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    gap: theme.spacing(1),
    flex: 1,
  },
  titleIcon: {
    color: "var(--ikaro-orange-primary)",
    flexShrink: 0,
  },
  titleText: {
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  cellChip: {
    marginLeft: theme.spacing(1),
    backgroundColor: "var(--ikaro-orange-light)",
    color: "var(--ikaro-orange-primary)",
    fontWeight: 600,
    maxWidth: 360,
  },
  closeBtn: {
    color: "var(--ikaro-gray-dark)",
  },
  dialogContent: {
    padding: 0,
    minWidth: 800,
    height: "70vh",
    display: "flex",
  },
  sidebar: {
    width: 240,
    backgroundColor: "var(--ikaro-gray-light)",
    borderRight: "1px solid var(--ikaro-gray-medium)",
    display: "flex",
    flexDirection: "column",
  },
  sidebarItem: {
    padding: theme.spacing(2),
    cursor: "pointer",
    borderBottom: "1px solid var(--ikaro-gray-medium)",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-light)",
    },
    "&.active": {
      backgroundColor: "var(--ikaro-orange-primary)",
      color: "white",
      fontWeight: "bold",
    },
  },
  contentArea: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "auto",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    color: "var(--ikaro-orange-primary)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  description: {
    color: "var(--ikaro-gray-dark)",
    marginBottom: theme.spacing(2),
  },
  currentNameBox: {
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: "var(--ikaro-bg-surface)",
    border: "1px solid var(--ikaro-border-default)",
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  currentNameLabel: {
    color: "var(--ikaro-gray-dark)",
    fontWeight: 600,
  },
  currentNameValue: {
    fontWeight: 700,
  },
  inputWrap: {
    position: "relative",
  },
  counter: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: 12,
    color: "var(--ikaro-gray-dark)",
    background: "var(--ikaro-bg-input)",
    padding: "2px 6px",
    borderRadius: 6,
    pointerEvents: "none",
  },
  textField: {
    "& .MuiInputBase-root": {
      backgroundColor: "var(--ikaro-bg-input)",
    },
  },
  helper: {
    marginTop: theme.spacing(0.75),
    fontSize: 12,
    color: "var(--ikaro-gray-dark)",
  },
  footer: {
    padding: theme.spacing(2, 3),
    borderTop: "1px solid var(--ikaro-gray-medium)",
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    backgroundColor: "var(--ikaro-gray-light)",
  },
  cancelButton: {
    color: "var(--ikaro-gray-dark)",
    borderColor: "var(--ikaro-gray-medium)",
  },
  saveButton: {
    backgroundColor: "var(--ikaro-orange-primary) !important",
    color: "white !important",
    "&:hover": {
      backgroundColor: "var(--ikaro-orange-hover) !important",
    },
    "&.Mui-disabled": {
      backgroundColor: "var(--ikaro-gray-medium) !important",
      color: "var(--ikaro-text-muted) !important",
    },
  },
}));

const ALLOWED_NAME_REGEX = /^[\p{L}\p{N} ()-]+$/u;
const MAX_LEN = 60;

function validateCellName(name) {
  const trimmed = (name || "").trim();
  if (trimmed.length < 1 || trimmed.length > MAX_LEN) {
    return `O nome deve ter entre 1 e ${MAX_LEN} caracteres.`;
  }
  if (!ALLOWED_NAME_REGEX.test(trimmed)) {
    return "Use apenas letras, números, espaços, hífens e parênteses.";
  }
  return null;
}

export default function CellSettingsModal({ open, onClose, onSuccessToast, onErrorToast }) {
  const classes = useStyles();
  const API_BASE_URL = getApiBaseUrl();
  const { token, cell, updateCellName } = useAuth();

  const [selectedTab, setSelectedTab] = React.useState("rename");
  const currentName = cell?.title || "";

  const [newName, setNewName] = React.useState(currentName);
  const [saving, setSaving] = React.useState(false);
  const [inlineError, setInlineError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setSelectedTab("rename");
      setNewName(currentName);
      setInlineError(null);
      setSaving(false);
    }
  }, [open, currentName]);

  const validationError = validateCellName(newName);
  const unchanged = newName.trim() === currentName.trim();
  const canSave = !!cell?.value && !saving && !unchanged && !validationError;

  const handleSave = async () => {
    if (!cell?.value) return;
    setInlineError(null);

    const err = validateCellName(newName);
    if (err) {
      setInlineError(err);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/cells/${cell.value}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: newName.trim() }),
      });

      if (response.ok) {
        const updated = await response.json();
        updateCellName(cell.value, updated?.name || newName.trim());
        if (onSuccessToast) onSuccessToast("Célula renomeada com sucesso");
        onClose();
        return;
      }

      const data = await response.json().catch(() => ({}));
      const detail = data?.detail;
      if (response.status === 409) {
        setInlineError(
          typeof detail === "string"
            ? detail
            : "Já existe outra célula com este nome.",
        );
      } else if (response.status === 400) {
        setInlineError(
          typeof detail === "string"
            ? detail
            : "Nome inválido. Verifique as regras de nomenclatura.",
        );
      } else if (response.status === 403) {
        setInlineError("Você não tem permissão para editar esta célula.");
      } else {
        if (onErrorToast) {
          onErrorToast(
            typeof detail === "string"
              ? detail
              : "Falha ao renomear a célula. Tente novamente.",
          );
        }
      }
    } catch (e) {
      if (onErrorToast) onErrorToast("Falha ao renomear a célula. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleLeft}>
          <Settings className={classes.titleIcon} />
          <Typography variant="h6" className={classes.titleText}>
            Configurações da Célula
          </Typography>
          {currentName ? (
            <Chip
              label={currentName}
              size="small"
              className={classes.cellChip}
              title={currentName}
            />
          ) : null}
        </Box>
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          className={classes.closeBtn}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.sidebar}>
          <Box
            className={`${classes.sidebarItem} ${
              selectedTab === "rename" ? "active" : ""
            }`}
            onClick={() => setSelectedTab("rename")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setSelectedTab("rename");
            }}
            aria-label="Renomear célula"
          >
            <Edit fontSize="small" />
            <Typography variant="body1">Renomear célula</Typography>
          </Box>
        </Box>

        <Box className={classes.contentArea}>
          {selectedTab === "rename" ? (
            <>
              <Typography className={classes.sectionTitle}>
                <Edit fontSize="small" />
                Renomear célula
              </Typography>
              <Typography variant="body2" className={classes.description}>
                Altere o nome de exibição desta célula. O novo nome será refletido
                em todos os locais onde a célula aparece (seletor no header,
                filtros, listagens e breadcrumbs).
              </Typography>

              <Box className={classes.currentNameBox}>
                <Typography variant="body2" className={classes.currentNameLabel}>
                  Nome atual:
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.currentNameValue}
                  title={currentName}
                >
                  {currentName || "-"}
                </Typography>
              </Box>

              <Box mb={1}>
                <Typography variant="body2" style={{ fontWeight: 700 }}>
                  Novo nome da célula *
                </Typography>
              </Box>
              <Box className={classes.inputWrap}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  inputProps={{ maxLength: MAX_LEN, "aria-label": "Novo nome da célula", "data-wizard": "cell-name-input" }}
                  className={classes.textField}
                  error={!!inlineError || !!validationError}
                  helperText={inlineError || validationError || " "}
                />
                <span className={classes.counter}>
                  {Math.min((newName || "").length, MAX_LEN)}/{MAX_LEN}
                </span>
              </Box>
              <Typography className={classes.helper}>
                Use um nome curto e descritivo. Permitidos: letras, números,
                espaços, hífens e parênteses.
              </Typography>
            </>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions className={classes.footer}>
        <Button onClick={onClose} variant="outlined" className={classes.cancelButton}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          className={classes.saveButton}
          disabled={!canSave}
          data-wizard="cell-save-btn"
        >
          Salvar alterações
        </Button>
      </DialogActions>
    </Dialog>
  );
}

